import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Flag, ShieldCheck, Sparkles, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { navigateWithTransition } from "@/lib/viewTransition";
import { AuthLayout } from "@/components/AuthLayout";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Button } from "@/components/ui/Button";

const FEATURES = [
  { icon: Flag, label: "Arbitra partidos de los clubes de la plataforma" },
  { icon: Trophy, label: "Los organizadores te asignan a los partidos de sus campeonatos" },
  { icon: ShieldCheck, label: "Revisamos cada solicitud personalmente, sin bots" },
];

const schema = z
  .object({
    nombre: z.string().min(2, "Mínimo 2 caracteres"),
    apellidos: z.string().optional(),
    email: z.string().email("Email inválido"),
    telefono: z.string().min(6, "Teléfono inválido"),
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string(),
    mensaje: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
type FormValues = z.infer<typeof schema>;

function LoginLink() {
  const navigate = useNavigate();
  return (
    <a
      href="/login"
      onClick={(e) => {
        e.preventDefault();
        navigateWithTransition(navigate, "/login");
      }}
      className="font-medium text-brand-600 hover:text-brand-700"
    >
      Inicia sesión
    </a>
  );
}

// Registro propio de árbitros: mismo flujo que el de proveedores (RegisterProviderPage) — se
// valida a mano y solo entonces se crea la cuenta, con la contraseña que se elige aquí.
export function RegisterRefereePage() {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(data: FormValues) {
    setSubmitError(null);
    const { confirmPassword: _confirm, ...payload } = data;
    try {
      await api.post("/referee-registrations", payload);
      setSent(true);
    } catch (error: any) {
      setSubmitError(error?.response?.data?.error ?? "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    }
  }

  return (
    <AuthLayout
      headline="¿Eres árbitro de pádel? Únete."
      subtext="Regístrate como árbitro y los organizadores de campeonatos podrán asignarte a sus partidos. Nuestro equipo revisará tu solicitud antes de activar tu cuenta."
      features={FEATURES}
      formEyebrow={
        !sent && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
            <Sparkles className="h-3.5 w-3.5" />
            Registro de árbitros
          </span>
        )
      }
    >
      {sent ? (
        <div className="flex flex-col items-start gap-4">
          <span className="auth-in flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h2 className="auth-in text-2xl font-bold text-ink-900" style={{ animationDelay: "60ms" }}>
            ¡Solicitud enviada!
          </h2>
          <p className="auth-in text-sm text-ink-500" style={{ animationDelay: "120ms" }}>
            Gracias por tu interés. Nuestro equipo revisará tu solicitud y te avisará por correo
            cuando tu cuenta de árbitro esté activa.
          </p>
          <div className="auth-in" style={{ animationDelay: "180ms" }}>
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                navigateWithTransition(navigate, "/login");
              }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Volver al inicio de sesión
            </a>
          </div>
        </div>
      ) : (
        <>
          <h2 className="auth-in text-2xl font-bold text-ink-900">Cuéntanos quién eres</h2>
          <p className="auth-in mt-1 mb-6 text-sm text-ink-400" style={{ animationDelay: "60ms" }}>
            Los registros se validan manualmente por nuestro equipo — no se crea ninguna cuenta
            automáticamente. La contraseña que definas aquí será la que uses para iniciar sesión
            una vez que aprobemos tu solicitud.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="auth-in grid grid-cols-2 gap-4" style={{ animationDelay: "110ms" }}>
              <Input id="nombre" label="Nombre" error={errors.nombre?.message} {...register("nombre")} />
              <Input id="apellidos" label="Apellidos" error={errors.apellidos?.message} {...register("apellidos")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "150ms" }}>
              <Input id="email" type="email" label="Email" error={errors.email?.message} {...register("email")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "190ms" }}>
              <Input id="telefono" label="Teléfono" error={errors.telefono?.message} {...register("telefono")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "230ms" }}>
              <PasswordInput id="password" label="Contraseña" error={errors.password?.message} {...register("password")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "270ms" }}>
              <PasswordInput
                id="confirmPassword"
                label="Confirmar contraseña"
                error={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />
            </div>
            <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "310ms" }}>
              <label htmlFor="mensaje" className="text-sm font-medium text-ink-600">
                Experiencia o comentarios (opcional)
              </label>
              <textarea
                id="mensaje"
                rows={3}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                {...register("mensaje")}
              />
            </div>

            {submitError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{submitError}</p>}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="auth-in mt-2 w-full transition-[transform,box-shadow,filter] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:brightness-105 active:translate-y-0 active:brightness-95"
              style={{ animationDelay: "350ms" }}
            >
              {isSubmitting ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </form>

          <p className="auth-in mt-6 text-center text-sm text-ink-400" style={{ animationDelay: "390ms" }}>
            ¿Ya tienes acceso? <LoginLink />
          </p>
        </>
      )}
    </AuthLayout>
  );
}
